import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import Curtains from "@/components/Curtains";
import AppStoreButton from "@/components/AppStoreButton";
import { playCurtainSwoosh } from "@/lib/curtainSound";

const PRELOAD = ["/hero/theatre-wide.jpg", "/hero/theatre-portrait.jpg", "/manifesto/dream-wide.jpg"];

const EASE = [0.16, 1, 0.3, 1];
const rise = (delay) => ({
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  transition: { duration: 1.4, ease: EASE, delay },
});

export default function WelcomePage() {
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    PRELOAD.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const explore = () => {
    if (leaving) return;
    setLeaving(true);
    try {
      playCurtainSwoosh(0.5);
    } catch (e) {
      /* ses desteklenmiyorsa sessiz devam et */
    }
    setTimeout(() => navigate("/home", { state: { curtain: true } }), 650);
  };

  return (
    <div data-testid="welcome-page" className="relative h-[100dvh] w-full overflow-hidden bg-[#1a0508] text-ivory-light">
      <Curtains open={false} heart />
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
        className="absolute inset-0 bg-black pointer-events-none z-20"
      />

      <motion.div
        animate={{ opacity: leaving ? 0 : 1, y: leaving ? -10 : 0 }}
        transition={{ duration: 0.55, ease: EASE }}
        className="absolute inset-0 z-10 flex flex-col"
      >
        <header className="flex items-center justify-between px-6 sm:px-10 h-20 sm:h-24">
          <motion.div {...rise(0.9)} className="flex items-center gap-1.5">
            <span className="font-cinzel text-sm sm:text-base tracking-[0.35em] text-ivory-light/80">FIVE</span>
            <Star className="w-2.5 h-2.5 fill-gold text-gold" aria-hidden="true" />
            <span className="font-cinzel text-sm sm:text-base tracking-[0.35em] text-ivory-light/80">STAR</span>
          </motion.div>
          <motion.div {...rise(1.0)}>
            <AppStoreButton variant="light" testid="welcome-appstore-button" className="!px-4 !py-2 sm:!px-5 sm:!py-2.5 !text-[12px] sm:!text-[13px] !bg-ivory-light/90 backdrop-blur" />
          </motion.div>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 -mt-6">
          <motion.h1 {...rise(0.6)} data-testid="welcome-title" className="drop-shadow-[0_6px_30px_rgba(0,0,0,0.55)] leading-none">
            <span className="block font-serif font-semibold tracking-[-0.02em] text-[3.6rem] sm:text-[6.5rem] lg:text-[8rem] text-ivory-light">
              FIVE STAR
            </span>
            <span className="block font-pinyon text-5xl sm:text-7xl lg:text-8xl text-rose-soft -mt-3 sm:-mt-6 lg:-mt-8 pl-14 sm:pl-28 lg:pl-40">
              Concierge Dating
            </span>
          </motion.h1>
          <motion.div {...rise(1.25)} className="mt-6 sm:mt-8 flex items-center gap-4">
            <span className="h-px w-8 sm:w-14 bg-gold/70" />
            <p data-testid="welcome-tagline" className="font-serif font-medium text-xl sm:text-3xl lg:text-[2.1rem] text-gold-subtle drop-shadow-[0_2px_14px_rgba(0,0,0,0.6)]">
              It&rsquo;s time to tell a better love story
            </p>
            <span className="h-px w-8 sm:w-14 bg-gold/70" />
          </motion.div>
        </div>

        <motion.div {...rise(1.7)} className="pb-10 sm:pb-14 flex justify-center">
          <button
            type="button"
            data-testid="welcome-explore-button"
            onClick={explore}
            className="group relative inline-flex items-center gap-3 px-9 py-4 rounded-full border border-gold/60 text-ivory-light font-sans text-[13px] tracking-[0.3em] uppercase backdrop-blur-sm bg-black/20 hover:bg-gold hover:text-burgundy-deep hover:border-gold transition-colors duration-500"
          >
            Keşfet
            <ArrowDown className="w-4 h-4 transition-transform duration-500 group-hover:translate-y-1" aria-hidden="true" />
            <span className="absolute -inset-px rounded-full border border-gold/30 animate-ping-slow pointer-events-none" />
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
